import { useEffect } from 'react';

import { pushToDataLayer } from 'services/gtmService';
import { useBraze } from 'hooks/useBraze';
import { SaveAd } from '../../../../components/Toolkit/SaveAd/SaveAd';
import { accountsApi } from '../../../../api/accountsApi';
import { fireToast } from 'helpers/Toasts';
import { useUserContext } from '../../../../contexts/UserContext';
import StatusCode from 'status-code-enum';
import { useAuthOptions } from 'hooks/useAuthOptions';
import { formatAuthorizationHeader } from 'helpers/auth';

interface ShowAndToggleSaveAdProps {
  adId: number;
  isViewingOwnAd?: boolean;
  userSaved?: boolean;
  isUnderlined?: boolean;
  isAdSaved?: boolean;
  className?: string;
  setIsAdSaved?: (t: boolean) => void;
  isPreview?: boolean;
  subsection?: string;
  sellerType?: string;
}

function ShowAndToggleSaveAd({
  adId,
  isViewingOwnAd = false,
  isUnderlined = false,
  isAdSaved,
  setIsAdSaved,
  className,
  isPreview,
  subsection,
  sellerType,
}: ShowAndToggleSaveAdProps) {
  const { user } = useUserContext();
  const { handleLogin, accessToken } = useAuthOptions();
  const { logBrazeSaveAdEvent } = useBraze();

  useEffect(() => {
    if (!user?.id) {
      setIsAdSaved && setIsAdSaved(false);
    }
    // TODO: Clean up this effect's dependencies. We're disabling this lint error for now so we can
    // clean up the lint logs. Ideally we should rewrite this code to be less error prone and trust
    // the lint rule's judgement.
    // https://distilledsch.tpondemand.com/RestUI/Board.aspx#page=userstory/98606
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  function handleUserEvent() {
    user?.id ? triggerToggleSave() : handleLogin();
  }

  async function triggerToggleSave() {
    const res = isAdSaved !== undefined && (await handleRequest(isAdSaved));
    if (res) {
      setIsAdSaved && setIsAdSaved(!isAdSaved);
    }
    if (!isAdSaved) {
      pushToDataLayer({
        event: 'create_saved_ad',
      });
      logBrazeSaveAdEvent(subsection, sellerType);
    } else {
      pushToDataLayer({
        event: 'delete_saved_ad',
      });
    }
  }

  async function handleRequest(savedStatus: boolean) {
    try {
      const { status } = savedStatus
        ? await accountsApi.unsaveAd(
            adId,
            formatAuthorizationHeader(accessToken),
          )
        : await accountsApi.saveAd(
            adId,
            formatAuthorizationHeader(accessToken),
          );
      const requestStatus = status === StatusCode.SuccessOK;
      if (requestStatus) {
        if (!savedStatus) {
          fireToast({
            type: 'SUCCESS',
            text: 'Added to your Saved Ads',
          });
        } else {
          fireToast({
            type: 'SUCCESS',
            text: 'Removed from your Saved Ads',
          });
        }
      }
      return requestStatus;
    } catch (_error) {
      fireToast({
        type: 'ERROR',
        text: 'Oops! Something went wrong, please try again later',
      });
      return false;
    }
  }

  return !isViewingOwnAd ? (
    <SaveAd
      toggleSave={!isPreview ? handleUserEvent : undefined}
      isAdSaved={isAdSaved}
      isUnderlined={isUnderlined}
      className={className}
      iconSizeDesktop="SM"
    />
  ) : null;
}

export { ShowAndToggleSaveAd };
